import { Card, Button, Input, Label } from '@suscri/ui';
import { ArrowLeft, Mail, CheckCircle2, XCircle, Send } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { trpc } from '../../lib/trpc';

// Configuración SMTP desde el panel admin (sin redeploy).
export default function EmailConfigPage() {
  const navigate = useNavigate();
  const [token] = useState<string | null>(() =>
    localStorage.getItem('adminToken')
  );

  const [form, setForm] = useState({
    host: '',
    port: '587',
    user: '',
    pass: '',
    from: '',
    secure: false,
  });
  const [testTo, setTestTo] = useState('');
  const [feedback, setFeedback] = useState<{
    ok: boolean;
    text: string;
  } | null>(null);

  const { data: config, refetch } = trpc.admin.emailGetConfig.useQuery(
    { token: token || '' },
    { enabled: Boolean(token) }
  );
  const setConfig = trpc.admin.emailSetConfig.useMutation();
  const testConfig = trpc.admin.emailTest.useMutation();

  if (!token) {
    return (
      <div className="p-6">
        No autorizado. Inicia sesión como administrador.
      </div>
    );
  }

  const save = async () => {
    setFeedback(null);
    try {
      await setConfig.mutateAsync({
        token,
        host: form.host || undefined,
        port: form.port || undefined,
        user: form.user || undefined,
        from: form.from || undefined,
        secure: form.secure,
        ...(form.pass ? { pass: form.pass } : {}),
      });
      setForm(f => ({ ...f, pass: '' }));
      await refetch();
      setFeedback({ ok: true, text: 'Configuración guardada.' });
    } catch (e) {
      setFeedback({
        ok: false,
        text: e instanceof Error ? e.message : 'No se pudo guardar.',
      });
    }
  };

  const test = async () => {
    setFeedback(null);
    const r = await testConfig.mutateAsync({
      token,
      ...(testTo ? { to: testTo } : {}),
    });
    setFeedback({ ok: r.ok, text: r.message });
  };

  const field = (
    k: keyof typeof form,
    label: string,
    type = 'text',
    placeholder = ''
  ) => (
    <div>
      <Label htmlFor={k}>{label}</Label>
      <Input
        id={k}
        type={type}
        autoComplete="off"
        placeholder={placeholder}
        value={form[k] as string}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setForm(f => ({ ...f, [k]: e.target.value }))
        }
      />
    </div>
  );

  return (
    <div className="app-shell min-h-screen">
      <div className="border-b border-border bg-background/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center gap-4 py-5">
            <Button
              variant="outline"
              onClick={() => navigate('/admin')}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" /> Volver
            </Button>
            <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-xl flex items-center justify-center shadow-lg">
              <Mail className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Email (SMTP)</h1>
              <p className="text-sm text-gray-600">
                Necesario para bienvenida, recuperación de contraseña y alertas.
                Sin configurar, esos emails no se envían.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        <Card className="rounded-xl border-0 bg-white p-6 shadow-lg">
          <h3 className="text-base font-semibold text-gray-900 mb-4">
            Estado actual
          </h3>
          {config?.configured ? (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 border border-emerald-200 px-3 py-1 text-sm font-semibold text-emerald-700">
              <CheckCircle2 className="h-4 w-4" /> Email ACTIVO — {config.host}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 border border-amber-200 px-3 py-1 text-sm font-semibold text-amber-700">
              <XCircle className="h-4 w-4" /> Sin configurar
            </span>
          )}
          {config?.configured && (
            <p className="mt-3 text-sm text-gray-600">
              Remitente: <code>{config.from || config.user}</code> · Puerto{' '}
              {config.port} · {config.secure ? 'SSL/TLS' : 'STARTTLS'}
            </p>
          )}
        </Card>

        <Card className="rounded-xl border-0 bg-white p-6 shadow-lg space-y-4">
          <h3 className="text-base font-semibold text-gray-900">
            Servidor SMTP
          </h3>
          {field('host', 'Host', 'text', 'smtp.tuproveedor.com')}
          <div className="grid grid-cols-2 gap-4">
            {field('port', 'Puerto', 'text', '587')}
            <div className="flex items-end pb-2">
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={form.secure}
                  onChange={e =>
                    setForm(f => ({ ...f, secure: e.target.checked }))
                  }
                />
                Conexión SSL/TLS (puerto 465)
              </label>
            </div>
          </div>
          {field('user', 'Usuario', 'text', 'usuario@dominio.com')}
          {field(
            'pass',
            'Contraseña',
            'password',
            config?.passMasked ? `(guardada: ${config.passMasked})` : ''
          )}
          {field(
            'from',
            'Remitente (From)',
            'text',
            'SuscriSoft <no-reply@suscri.net>'
          )}
          <p className="text-xs text-gray-500">
            Los campos vacíos no modifican lo guardado (salvo la contraseña, que
            solo se actualiza si escribes una nueva).
          </p>
          <div className="flex flex-wrap gap-3">
            <Button onClick={save} disabled={setConfig.isPending}>
              {setConfig.isPending ? 'Guardando…' : 'Guardar'}
            </Button>
            <div className="flex items-center gap-2">
              <Input
                placeholder="email para prueba (opcional)"
                value={testTo}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setTestTo(e.target.value)
                }
                className="w-56"
              />
              <Button
                variant="outline"
                onClick={test}
                disabled={testConfig.isPending}
                className="flex items-center gap-2"
              >
                <Send className="h-4 w-4" />
                {testConfig.isPending ? 'Probando…' : 'Probar'}
              </Button>
            </div>
          </div>
          {feedback && (
            <p
              className={`text-sm font-medium ${feedback.ok ? 'text-emerald-700' : 'text-red-700'}`}
            >
              {feedback.text}
            </p>
          )}
        </Card>
      </div>
    </div>
  );
}
